<template>
  <div class="relative min-h-screen">
    <!-- Main Content -->
    <div 
      v-if="page && page._id && pageLoaded" 
      class="transition-all duration-700 ease-in-out opacity-100"
    >
      <RenderBlog v-if="page.type === 'blog'" :page="page" :lang="currentLang" />
      <RenderCustom v-else-if="page.content || ['presskit', 'gamificacao', 'other'].includes(page.type)" :page="page" :lang="currentLang" />
    <template v-else>
      <!-- Navbar -->
      <component :is="navbarComponent"/>

      <div :class="{ 'theme-light': !isDarkBg }">

      <!-- Hero Section -->
      <div
          :style="{ backgroundColor: page.bgColor || page.color || '#0A0A0B' }"
          class="relative bg-gray-900 overflow-hidden">
        <div
            class="flex flex-col items-center justify-center relative w-full h-[450px] sm:h-[550px] md:h-[650px] lg:h-[700px] transition-opacity duration-700"
            :class="pageLoaded ? 'animate-fade-right delay-100' : ''">

          <div class="relative w-full h-full overflow-hidden">
            <template v-if="pageLoaded">
              <img
                  :src="page.image"
                  alt="Descrição da imagem"
                  class="w-full h-full object-cover md:object-cover object-center"
                  style="object-position: center center;"
              >
              <div class="absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t"
                   :style="{ background: `linear-gradient(to top, ${page.bgColor || page.color || '#0A0A0B'}, transparent)` }"></div>
            </template>
            <template v-else>
              <div class="w-full h-full bg-gray-700 animate-pulse"></div>
            </template>
          </div>
        </div>
        <div class="flex justify-center pt-10">
          <template v-if="pageLoaded">
            <div class="w-full max-w-6xl px-4">
              <!-- Desktop: flexbox com wrap -->
              <div class="hidden sm:flex flex-wrap justify-center items-center gap-3">
                <div
                    v-for="(button, index) in page.buttons"
                    :key="index"
                    class="flex-shrink-0"
                    v-memo="[button.link, button.text, button.image]">
                  <a
                      :href="button.link"
                      target="_blank"
                      class="flex items-center justify-center text-white rounded-md hover:opacity-80 transition-opacity whitespace-nowrap">
                    <img
                        v-if="button.image"
                        :src="button.image"
                        alt="Button Image"
                        class="h-12 sm:h-14 mr-2"
                        loading="lazy"
                        decoding="async" />
                    <span class="text-sm sm:text-base">{{ button.text }}</span>
                  </a>
                </div>
              </div>

              <!-- Mobile: grid 2 colunas -->
              <div class="sm:hidden flex flex-wrap justify-center gap-2 max-w-sm mx-auto">
                <div
                    v-for="(button, index) in page.buttons"
                    :key="index"
                    class="flex-shrink-0"
                    v-memo="[button.link, button.text, button.image]">
                  <a
                      :href="button.link"
                      target="_blank"
                      class="flex flex-col items-center justify-center text-white rounded-md hover:opacity-80 transition-opacity p-2 text-center">
                    <img
                        v-if="button.image"
                        :src="button.image"
                        alt="Button Image"
                        class="w-40 object-contain flex-shrink-0 mb-1"
                        loading="lazy"
                        decoding="async" />
                    <span class="text-xs leading-tight">{{ button.text }}</span>
                  </a>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="hidden sm:flex space-x-3 justify-center flex-wrap gap-3">
              <div v-for="index in 3" :key="index" class="h-12 sm:h-14 w-32 sm:w-48 bg-gray-700 animate-pulse rounded-md"></div>
            </div>
            <div class="sm:hidden grid grid-cols-2 gap-3 w-full max-w-xs">
              <div v-for="index in 4" :key="index" class="h-16 w-full bg-gray-700 animate-pulse rounded-md"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- YOUTUBE Section -->
      <div :style="{ backgroundColor: page.bgColor || page.color || '#0A0A0B' }"
           class="flex justify-center items-center py-8 sm:py-12 lg:py-16 transition-opacity duration-700"
           :class="pageLoaded ? 'animate-fade-right delay-300' : ''">
        <div class="py-2 mx-4 sm:mx-8 lg:mx-12 rounded-md max-w-6xl justify-center text-white lg:flex md:items-center">
          <template v-if="pageLoaded">
            <div class="w-full">
              <p class="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-white font-roboto text-center leading-tight">
                <span v-if="isHtmlString(youtubeTitle)" v-html="youtubeTitle"></span>
                <span v-else>{{ youtubeTitle }}</span>
              </p>
              <p class="mt-4 sm:mt-6 font-light py-4 sm:py-6 lg:py-8 text-base sm:text-lg font-roboto text-center leading-relaxed">
                <span v-if="isHtmlString(youtubeDescription)" v-html="youtubeDescription"></span>
                <span v-else>{{ youtubeDescription }}</span>
              </p>
              <div class="w-full max-w-4xl mx-auto">
                <div class="aspect-video w-full flex items-center justify-center shadow-none rounded-2xl sm:rounded-3xl overflow-hidden border-2 sm:border-4 border-gray-700">
                  <iframe :src="youtubeEmbedLink" frameborder="0" allowfullscreen class="w-full h-full"></iframe>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col items-center w-full">
              <div class="h-8 sm:h-10 w-64 sm:w-80 bg-gray-700 animate-pulse rounded mb-4 sm:mb-6"></div>
              <div class="h-5 sm:h-6 w-80 sm:w-96 bg-gray-700 animate-pulse rounded mb-6 sm:mb-8"></div>
              <div class="aspect-video w-full max-w-4xl bg-gray-700 animate-pulse rounded-2xl sm:rounded-3xl border-2 sm:border-4 border-gray-700"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- CAROUSEL Section -->
      <div
          v-if="pageLoaded && (page.title2 || page.description2 || (page.images && page.images.length > 0))"
          :style="{ backgroundColor: page.bgColor || page.color || '#0A0A0B' }"
          class="bg-white justify-center flex flex-col items-center py-8 sm:py-12 lg:py-16 text-[#00a0f1] transition-opacity duration-700"
          :class="pageLoaded ? 'animate-fade-right delay-500' : ''"
      >
        <div class="mx-4 sm:mx-8 lg:mx-12 max-w-6xl">
          <h1
              v-if="page.title2"
              class="mt-2 font-black py-3 text-white text-2xl sm:text-3xl font-roboto md:text-4xl lg:text-5xl text-center leading-tight"
              :style="{ color: page.colorSecondary }"
          >
            <span v-if="isHtmlString(page.title2)" v-html="page.title2"></span>
            <span v-else>{{ page.title2 }}</span>
          </h1>
          <p
              v-if="page.description2"
              class="mt-3 mx-4 sm:mx-8 md:mx-24 lg:mx-36 py-3 text-center text-white items-center font-roboto text-sm sm:text-base md:text-lg leading-relaxed"
          >
            <span v-if="isHtmlString(page.description2)" v-html="page.description2"></span>
            <span v-else>{{ page.description2 }}</span>
          </p>
        </div>
        <div class="flex justify-center items-center w-full" v-if="page.images && page.images.length > 0">
          <div class="mx-4 sm:mx-8 lg:mx-0 flex flex-col gap-8 sm:gap-12 w-full">
            <CarrosselMobile
                v-if="page.platform === 'mobile'"
                :images="page.images"
                :pageColor="page.bgColor || page.color || '#0A0A0B'"
            />
            <CarrosselDesktop
                v-else
                :images="page.images"
            />
          </div>
        </div>
      </div>
      <div
          v-else-if="!pageLoaded"
          :style="{ backgroundColor: page.bgColor || page.color || '#0A0A0B' }"
          class="bg-white justify-center flex flex-col items-center py-8 sm:py-12 lg:py-16 text-[#00a0f1] transition-opacity duration-700"
      >
        <div class="h-8 sm:h-10 w-64 sm:w-80 bg-gray-700 animate-pulse rounded mb-4 sm:mb-6 mx-auto"></div>
        <div class="h-5 sm:h-6 w-80 sm:w-96 bg-gray-700 animate-pulse rounded mb-6 sm:mb-8 mx-auto"></div>
        <div class="h-48 sm:h-64 w-80 sm:w-96 bg-gray-700 animate-pulse rounded-2xl"></div>
      </div>

      <!-- CTA Section -->
      <div :style="{ backgroundColor: page.bgColor || page.color || '#0A0A0B' }"
           class="flex justify-center items-center py-8 sm:py-12 lg:py-16 transition-opacity duration-700"
           :class="pageLoaded ? 'animate-fade-right delay-700' : ''">
        <div class="py-2 mx-4 sm:mx-8 lg:mx-12 rounded-md max-w-6xl justify-center text-white lg:flex lg:items-center lg:gap-8">
          <template v-if="pageLoaded">
            <div class="flex-1 mb-8 lg:mb-0">
              <p class="mt-2 font-black md:mt-6 py-3 text-2xl sm:text-3xl text-white font-roboto md:text-4xl lg:text-5xl text-center lg:text-start leading-tight">
                <span v-if="isHtmlString(page.title3)" v-html="page.title3"></span>
                <span v-else>{{ page.title3 }}</span>
              </p>
              <p class="mt-3 max-w-lg text-center py-3 font-roboto text-sm sm:text-base md:text-lg lg:text-start leading-relaxed">
                <span v-if="isHtmlString(page.description3)" v-html="page.description3"></span>
                <span v-else>{{ page.description3 }}</span>
              </p>
              <div class="hidden sm:flex flex-wrap justify-center lg:justify-start items-center gap-3 mt-6">
                <div
                    v-for="(button, index) in page.buttons"
                    :key="index"
                    class="flex-shrink-0"
                    v-memo="[button.link, button.text, button.image]">
                  <a
                      :href="button.link"
                      target="_blank"
                      class="flex items-center justify-center text-white rounded-md hover:opacity-80 transition-opacity whitespace-nowrap">
                    <img
                        v-if="button.image"
                        :src="button.image"
                        alt="Button Image"
                        class="h-10 sm:h-12 mr-2"
                        loading="lazy"
                        decoding="async" />
                    <span class="text-sm sm:text-base">{{ button.text }}</span>
                  </a>
                </div>
              </div>

              <!-- Mobile: grid 2 colunas -->
              <div class="sm:hidden grid grid-cols-2 gap-2 mt-6 max-w-sm mx-auto">
                <div
                    v-for="(button, index) in page.buttons"
                    :key="index"
                    class="flex justify-center"
                    v-memo="[button.link, button.text, button.image]">
                  <a
                      :href="button.link"
                      target="_blank"
                      class="flex flex-col items-center justify-center text-white rounded-md hover:opacity-80 transition-opacity p-2 text-center">
                    <img
                        v-if="button.image"
                        :src="button.image"
                        alt="Button Image"
                        class="w-40 object-contain flex-shrink-0 mb-1"
                        loading="lazy"
                        decoding="async" />
                    <span class="text-xs leading-tight">{{ button.text }}</span>
                  </a>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-center shadow-none overflow-hidden flex-shrink-0">
              <img class="rounded-2xl sm:rounded-3xl shadow-lg max-w-full h-auto w-full sm:w-80 lg:w-96" :src="page.imageCta" alt="CTA Imagem" />
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col gap-4 sm:gap-6 items-center w-full">
              <div class="h-8 sm:h-10 w-64 sm:w-80 bg-gray-700 animate-pulse rounded"></div>
              <div class="h-5 sm:h-6 w-80 sm:w-96 bg-gray-700 animate-pulse rounded"></div>
              <div class="h-48 sm:h-64 w-64 sm:w-80 bg-gray-700 animate-pulse rounded-2xl sm:rounded-3xl"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- Newsletter -->
      <div
          v-if="page.newsletterEnabled"
          class="py-8 sm:py-12 flex justify-center transition-opacity duration-700 bg-white"
          :class="pageLoaded ? 'animate-fade-right delay-[1100ms]' : ''"
      >
        <div
            class="w-full max-w-6xl mx-4 sm:mx-8 lg:mx-0 flex flex-col md:flex-row items-center gap-6 sm:gap-8"
        >
          <!-- Texto + descrição -->
          <div class="flex-1 text-center md:text-left">
            <h2
                class="text-xl sm:text-2xl md:text-3xl font-bold leading-tight"
                :style="{ color: page.colorSecondary || '#00a0f1' }"
            >
              Receba notícias do jogo em primeira mão
            </h2>
            <p class="mt-3 text-gray-700 text-sm sm:text-base md:max-w-sm leading-relaxed">
              Fique por dentro das novidades, lançamentos e promoções exclusivas.
            </p>
          </div>

          <form
              @submit.prevent="subscribeNewsletter"
              class="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full md:max-w-md"
          >
            <input
                v-model="newsletterEmail"
                type="email"
                required
                placeholder="Seu e-mail"
                class="flex-1 rounded-lg border-2 px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white"
            />
            <button
                type="submit"
                :disabled="isSubscribing"
                class="rounded-lg px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base font-semibold text-white hover:opacity-90 transition disabled:opacity-50"
                :style="{ backgroundColor: page.colorSecondary || '#00a0f1' }"
            >
              {{ isSubscribing ? 'Inscrevendo...' : 'Inscrever-se' }}
            </button>
          </form>
        </div>
      </div>

      <!-- Sponsors -->
      <div :style="{ backgroundColor: page.bgColor || page.color || '#0A0A0B' }"
           class="py-8 sm:py-12 lg:py-16 transition-opacity duration-700"
           :class="pageLoaded ? 'animate-fade-right delay-1000' : ''">
        <div class="mx-auto max-w-[1600px] px-4 sm:px-8">
          <div class="flex flex-wrap justify-center gap-4 sm:gap-6">
            <template v-if="pageLoaded">
              <div v-for="(group, groupIndex) in page.sponsorGroups" :key="groupIndex"
                   class="min-w-[240px] sm:min-w-[280px] max-w-fit p-3 sm:p-4 rounded-2xl">
                <h3 class="text-lg sm:text-xl font-semibold text-center text-white mb-3 sm:mb-4">{{ group.title }}</h3>
                <div class="flex flex-wrap justify-center gap-3 sm:gap-4">
                  <div v-for="(logo, logoIndex) in group.logos" :key="logoIndex"
                       class="flex items-center justify-center">
                    <img :src="logo" alt="Logo" class="w-24 sm:w-32 md:w-40 object-contain rounded-2xl" />
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div v-for="index in 2" :key="index" class="min-w-[240px] sm:min-w-[280px] max-w-fit p-3 sm:p-4 bg-white/5 rounded-2xl">
                <div class="h-5 sm:h-6 w-32 sm:w-48 bg-gray-700 animate-pulse rounded mb-4 sm:mb-6 mx-auto"></div>
                <div class="flex flex-wrap justify-center gap-3 sm:gap-4">
                  <div class="h-20 sm:h-24 w-24 sm:w-32 bg-gray-700 animate-pulse rounded-2xl"></div>
                  <div class="h-20 sm:h-24 w-24 sm:w-32 bg-gray-700 animate-pulse rounded-2xl"></div>
                  <div class="h-20 sm:h-24 w-24 sm:w-32 bg-gray-700 animate-pulse rounded-2xl"></div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      </div>

      <Footer
          :language="currentLang"
          :footer-logo="page.footer?.logo"
          :footer-social-links="page.footer?.socialLinks"
      />
    </template>
    </div>

    <!-- Skeleton Loader (Shown behind overlay when page is loading) -->
    <div 
      v-else
      class="w-full min-h-screen bg-[#0c0a0f] text-gray-400 overflow-hidden select-none pointer-events-none filter blur-sm"
    >
      <!-- Skeleton Navbar -->
      <div class="h-20 md:h-24 bg-white/[0.02] border-b border-white/[0.04] flex items-center justify-between px-6 md:px-20 animate-pulse">
        <div class="w-32 h-10 bg-white/10 rounded-xl"></div>
        <div class="hidden md:flex gap-8">
          <div class="w-16 h-4 bg-white/5 rounded-full"></div>
          <div class="w-16 h-4 bg-white/5 rounded-full"></div>
          <div class="w-16 h-4 bg-white/5 rounded-full"></div>
          <div class="w-16 h-4 bg-white/5 rounded-full"></div>
        </div>
        <div class="w-10 h-10 bg-white/5 rounded-full"></div>
      </div>

      <!-- Skeleton Hero -->
      <div class="relative h-[450px] sm:h-[650px] bg-white/[0.01] flex flex-col items-center justify-center p-6 border-b border-white/[0.03]">
        <div class="w-48 sm:w-64 h-32 sm:h-44 bg-white/5 rounded-2xl animate-pulse mb-8"></div>
        <div class="w-64 sm:w-96 h-6 bg-white/5 rounded-full animate-pulse mb-4"></div>
        <div class="w-40 h-14 bg-purple-900/20 rounded-xl animate-pulse"></div>
      </div>

      <!-- Skeleton Grid Content -->
      <div class="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 gap-12">
        <div class="space-y-6">
          <div class="w-2/3 h-8 bg-white/5 rounded-lg animate-pulse"></div>
          <div class="space-y-3">
            <div class="w-full h-4 bg-white/5 rounded-full animate-pulse"></div>
            <div class="w-full h-4 bg-white/5 rounded-full animate-pulse"></div>
            <div class="w-4/5 h-4 bg-white/5 rounded-full animate-pulse"></div>
          </div>
          <div class="w-full aspect-video bg-white/5 rounded-2xl animate-pulse"></div>
        </div>
        <div class="space-y-6">
          <div class="w-full aspect-video bg-white/5 rounded-2xl animate-pulse"></div>
          <div class="w-1/2 h-8 bg-white/5 rounded-lg animate-pulse"></div>
          <div class="space-y-3">
            <div class="w-full h-4 bg-white/5 rounded-full animate-pulse"></div>
            <div class="w-3/4 h-4 bg-white/5 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading Overlay (Translucent with Backdrop Blur) -->
    <div 
      v-if="!pageLoaded" 
      class="fixed inset-0 w-full h-full flex flex-col items-center justify-center bg-black/40 backdrop-blur-md z-[9999]"
    >
      <div class="flex flex-col items-center gap-6 relative">
        <!-- Pulse Glow behind logo -->
        <div class="absolute inset-0 bg-purple-500/10 rounded-full blur-3xl w-32 h-32 -translate-x-4 -translate-y-4 animate-pulse duration-2000"></div>
        
        <!-- Animated Logo Container -->
        <div class="relative z-10 animate-pulse duration-1500">
          <img :src="defaultLogo" alt="Mito Games" class="h-20 w-auto object-contain drop-shadow-[0_0_25px_rgba(168,85,247,0.35)]" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import defaultLogo from '@/assets/logo.png';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@vueuse/head';
import { ElMessage } from 'element-plus';
import api from '@/services/api';
import Footer from '@/components/sitegame/Footer.vue';
import NavbarGamificacao from '@/components/sitegame/Navbar.vue';
import NavbarEntreterimento from '@/components/sitegamificacao/Navbar.vue';
import CarrosselDesktop from "@/components/sitegame/CarrosselDesktop.vue";
import CarrosselMobile from "@/components/sitegame/CarrosselMobile.vue";
import RenderBlog from './RenderBlog.vue';
import RenderCustom from './RenderCustom.vue';

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id);
const currentLang = computed(() => (route.path.startsWith('/en') || route.query.lang === 'en') ? 'en' : 'pt');

const page = ref({});
const pageLoaded = ref(false);
const newsletterEmail = ref('');
const isSubscribing = ref(false);

const isDarkBg = computed(() => {
  const color = page.value.bgColor || page.value.color;
  if (!color) return true;
  const hex = color.replace('#', '');
  if (hex.length === 3) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 128;
  } else if (hex.length === 6) {
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 128;
  }
  return true;
});

// Navbar dinâmico
const navbarComponent = computed(() => {
  if (page.value.type === 'project') return NavbarEntreterimento;
  if (page.value.type === 'game') return NavbarGamificacao;
  return null;
});

// Newsletter
const subscribeNewsletter = async () => {
  if (!newsletterEmail.value) {
    ElMessage.warning('Por favor, digite seu e-mail');
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(newsletterEmail.value)) {
    ElMessage.error('Por favor, digite um e-mail válido');
    return;
  }

  isSubscribing.value = true;

  try {
    const newsletterData = {
      email: newsletterEmail.value,
      pageId: page.value._id || id.value,
      pageName: page.value.title || 'Página MitoGames'
    };

    const response = await api.saveNewsletter(newsletterData);

    if (response.data.success) {
      ElMessage.success('E-mail cadastrado com sucesso! Obrigado por se inscrever!');
      newsletterEmail.value = '';
    } else {
      ElMessage.info(response.data.message || 'E-mail já cadastrado!');
    }

  } catch (error) {
    console.error('Erro ao cadastrar newsletter:', error);

    if (error.response?.status === 400) {
      ElMessage.error(error.response.data.error || 'Dados inválidos');
    } else {
      ElMessage.error('Erro ao cadastrar. Tente novamente mais tarde.');
    }
  } finally {
    isSubscribing.value = false;
  }
};

// Youtube – Lógica multilíngue
const youtubeLinkToShow = computed(() => {
  if (currentLang.value === 'en') {
    return page.value.youtubeLinkEn || page.value.youtubeLink || '';
  }
  return page.value.youtubeLink || page.value.youtubeLinkEn || '';
});

const youtubeTitle = computed(() => {
  if (currentLang.value === 'en') {
    return page.value.title1En || page.value.title1 || '';
  }
  return page.value.title1 || page.value.title1En || '';
});

const youtubeDescription = computed(() => {
  if (currentLang.value === 'en') {
    return page.value.description1En || page.value.description1 || '';
  }
  return page.value.description1 || page.value.description1En || '';
});

const youtubeEmbedLink = computed(() => {
  if (!youtubeLinkToShow.value) return '';
  const videoId = extractYouTubeVideoId(youtubeLinkToShow.value);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
});

function extractYouTubeVideoId(videoLink) {
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = videoLink ? videoLink.match(regex) : null;
  return match ? match[1] : null;
}

// Função utilitária para identificar se é HTML ou texto puro
function isHtmlString(str) {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
}

// Gerar session ID para analytics
function getSessionId() {
  let sessionId = localStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    localStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
}

// Função de tracking simples
async function trackPageView() {
  try {
    await api.trackAnalytics({
      pageUrl: window.location.pathname,
      pageTitle: document.title,
      pageId: page.value._id,
      pageType: page.value.type,
      customUrl: id.value,
      language: currentLang.value,
      userAgent: navigator.userAgent,
      sessionId: getSessionId(),
      referrer: document.referrer,
      timestamp: new Date()
    });
  } catch (error) {
    console.error('Erro ao rastrear visualização:', error);
  }
}

// Carregar página
const loadPage = async () => {
  pageLoaded.value = false;
  try {
    const response = await api.getPage(id.value);
    page.value = response.data;

    if (currentLang.value === 'en' && page.value.en) {
      Object.assign(page.value, {
        ...page.value,
        ...page.value.en
      });
    }

    setMetaTags();

    pageLoaded.value = true;
    trackPageView();

  } catch (error) {
    console.error('Erro ao carregar a página:', error);
    pageLoaded.value = true;
  }
};

// SEO dinâmico
function setMetaTags() {
  if (!page.value || !page.value.title) return;

  const title = page.value.title || 'MitoGames – Jogos e Projetos';
  const description = page.value.description1 || 'Conheça os jogos e projetos da MitoGames, novidades, lançamentos e mais!';
  const url = typeof window !== 'undefined' ? window.location.href : '';
  let image = '/logo.png';

  if (Array.isArray(page.value.image) && page.value.image.length > 0) {
    image = page.value.image[0];
  } else if (typeof page.value.image === 'string' && page.value.image) {
    image = page.value.image;
  }

  const category = page.value.category || '';
  const type = page.value.type || 'page';
  
  const baseKeywords = type === 'project'
    ? `projeto ${title}, case mito games, mito games, gamificação, ${category}`
    : `blog ${title}, mito games, artigo mito games, novidades`;
  const keywords = `${title}, Mito Games, ${baseKeywords}`;

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': type === 'project' ? 'CreativeWork' : 'Article',
    'name': title,
    'headline': title,
    'description': description,
    'image': image,
    'url': url,
    'author': {
      '@type': 'Organization',
      'name': 'Mito Games'
    },
    'publisher': {
      '@type': 'Organization',
      'name': 'Mito Games',
      'logo': {
        '@type': 'ImageObject',
        'url': url.split('/pages/')[0] + '/logo.png'
      }
    }
  };

  useHead({
    title,
    meta: [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { property: 'twitter:title', content: title },
      { property: 'twitter:description', content: description },
      { property: 'twitter:image', content: image },
      { property: 'twitter:card', content: 'summary_large_image' },
      { name: 'robots', content: 'index, follow' }
    ],
    link: [
      {rel: 'canonical', href: url}
    ],
    script: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(schemaData)
      }
    ]
  });
}

onMounted(() => {
  loadPage();
});

watch(() => route.fullPath, loadPage);
watch(page, setMetaTags);
</script>

<style>
.theme-light {
  color: #1E293B !important;
}
.theme-light .text-white,
.theme-light h1,
.theme-light h2,
.theme-light h3,
.theme-light h4,
.theme-light p {
  color: #0F172A !important;
}
.theme-light .text-gray-400,
.theme-light .text-slate-500,
.theme-light .font-light {
  color: #334155 !important;
}

.animate-fade-in-page {
  animation: fadeInPage 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes fadeInPage {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
